<template>
  <div class="module-manager">
    <!-- 搜索工作栏 -->
    <ContentWrap>
      <el-form ref="queryFormRef" :inline="true" :model="queryParams" class="demo-form-inline">
        <el-form-item label="模块标识" prop="name">
          <el-input
            v-model="queryParams.name"
            placeholder="请输入模块标识"
            clearable
            @keyup.enter="handleQuery"
          />
        </el-form-item>
        <el-form-item label="模块名称" prop="title">
          <el-input
            v-model="queryParams.title"
            placeholder="请输入模块名称"
            clearable
            @keyup.enter="handleQuery"
          />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select
            v-model="queryParams.status"
            placeholder="模块状态"
            clearable
            style="width: 120px"
          >
            <el-option
              v-for="item in statusOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="类型" prop="type">
          <el-select
            v-model="queryParams.type"
            placeholder="模块类型"
            clearable
            style="width: 120px"
          >
            <el-option
              v-for="item in typeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="handleQuery">
            <Icon icon="ep:search" class="mr-5px" /> 搜索
          </el-button>
          <el-button @click="resetQuery">
            <Icon icon="ep:refresh" class="mr-5px" /> 重置
          </el-button>
          <el-button
            v-hasPermi="['module:module:create']"
            type="primary"
            plain
            @click="openForm('create')"
          >
            <Icon icon="ep:plus" class="mr-5px" /> 新增
          </el-button>
        </el-form-item>
      </el-form>
    </ContentWrap>

    <!-- 列表 -->
    <ContentWrap>
      <el-table
        v-loading="loading"
        :data="list"
        :stripe="true"
        :show-overflow-tooltip="true"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55" />
        <el-table-column label="模块标识" prop="name" min-width="120" />
        <el-table-column label="模块名称" prop="title" min-width="150" />
        <el-table-column label="版本" prop="version" width="100" />
        <el-table-column label="类型" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="getTypeTagType(row.type)" size="small">
              {{ row.typeName }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="getStatusTagType(row.status)" size="small">
              {{ row.statusName }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="内置" width="70" align="center">
          <template #default="{ row }">
            <el-tag v-if="row.builtIn" type="warning" size="small">内置</el-tag>
            <el-tag v-else type="info" size="small">扩展</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="商家中心" min-width="180">
          <template #default="{ row }">
            <template v-if="row.hasMerchantCenter">
              <el-tag size="small" type="success">{{
                merchantCenterSummary(row).moduleId || '已支持'
              }}</el-tag>
              <span class="ml-6px text-12px text-[var(--el-text-color-secondary)]">
                {{
                  merchantCenterSummary(row).enabled === false ? '暂停新增' : merchantSceneText(row)
                }}
              </span>
            </template>
            <el-tag v-else size="small" type="info">无</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="核心业务模块" width="120" align="center">
          <template #default="{ row }">
            <el-switch
              :model-value="isHomeCoreModule(row)"
              :disabled="!checkPermi(['module:module:update'])"
              @change="(val: boolean) => toggleHomeCoreModule(row, val)"
            />
          </template>
        </el-table-column>
        <el-table-column label="排序" prop="sort" width="70" align="center" />
        <el-table-column label="描述" prop="description" min-width="200" show-overflow-tooltip />
        <el-table-column label="创建时间" width="170" align="center">
          <template #default="{ row }">
            <span>{{ formatDate(row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="280" fixed="right" align="center">
          <template #default="{ row }">
            <el-button
              v-if="
                row.status === ModuleStatusEnum.INSTALLED ||
                row.status === ModuleStatusEnum.DISABLED
              "
              v-hasPermi="['module:module:update']"
              link
              type="success"
              @click="handleEnable(row.id)"
            >
              启用
            </el-button>
            <el-button
              v-if="row.status === ModuleStatusEnum.ENABLED"
              v-hasPermi="['module:module:update']"
              link
              type="warning"
              @click="handleDisable(row.id)"
            >
              禁用
            </el-button>
            <el-button
              v-if="row.status === ModuleStatusEnum.NOT_INSTALLED"
              v-hasPermi="['module:module:update']"
              link
              type="primary"
              @click="handleInstall(row.id)"
            >
              安装
            </el-button>
            <el-button
              v-if="
                row.status === ModuleStatusEnum.INSTALLED ||
                row.status === ModuleStatusEnum.DISABLED
              "
              v-hasPermi="['module:module:delete']"
              link
              type="danger"
              @click="handleUninstall(row.id)"
            >
              卸载
            </el-button>
            <el-button
              v-hasPermi="['module:module:update']"
              link
              type="primary"
              @click="openForm('update', row.id)"
            >
              修改
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <!-- 分页 -->
      <Pagination
        v-model:limit="queryParams.pageSize"
        v-model:page="queryParams.pageNo"
        :total="total"
        @pagination="getList"
      />
    </ContentWrap>

    <!-- 表单弹窗 -->
    <ModuleForm ref="formRef" @success="getList" />
  </div>
</template>

<script lang="ts" setup>
import * as ModuleApi from '@/api/system/module/index'
import { ModuleStatusEnum, ModuleTypeEnum } from '@/api/system/module/index'
import ModuleForm from './ModuleForm.vue'
import { formatDate } from '@/utils/formatTime'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useRouter } from 'vue-router'
import { usePermissionStoreWithOut } from '@/store/modules/permission'
import { HOME_CORE_MODULE_LIMIT, isHomeCoreModuleEnabled } from '@/utils/moduleCard'
import { checkPermi } from '@/utils/permission'

defineOptions({ name: 'SystemModule' })

const { push } = useRouter()
const permissionStore = usePermissionStoreWithOut()

const loading = ref(true)
const list = ref<ModuleApi.ModuleVO[]>([])
const total = ref(0)
const queryFormRef = ref()
const formRef = ref()

const merchantCenterSummary = (
  row: ModuleApi.ModuleVO
): Partial<ModuleApi.MerchantCenterConfig> => {
  if (!row.merchantCenterConfig) return {}
  try {
    return JSON.parse(row.merchantCenterConfig)
  } catch {
    return {}
  }
}

const merchantSceneText = (row: ModuleApi.ModuleVO) => {
  const scenes = merchantCenterSummary(row).openScenes || []
  return scenes
    .map(
      (scene) =>
        ({
          ADMIN_INITIAL: '首批',
          ADMIN_APPEND: '追加',
          APP_ONBOARDING: '入驻',
          SYSTEM_RETRY: '补偿'
        })[scene] || scene
    )
    .join(' / ')
}

const queryParams = reactive({
  pageNo: 1,
  pageSize: 20,
  name: '',
  title: '',
  status: undefined as number | undefined,
  type: undefined as number | undefined,
  builtIn: undefined as boolean | undefined
})

/** 状态选项 */
const statusOptions = ref([
  { label: '未安装', value: ModuleStatusEnum.NOT_INSTALLED },
  { label: '已安装', value: ModuleStatusEnum.INSTALLED },
  { label: '已启用', value: ModuleStatusEnum.ENABLED },
  { label: '已禁用', value: ModuleStatusEnum.DISABLED },
  { label: '启动失败', value: ModuleStatusEnum.FAILED },
  { label: '卸载中', value: ModuleStatusEnum.UNINSTALLING }
])

/** 类型选项 */
const typeOptions = ref([
  { label: '系统模块', value: ModuleTypeEnum.SYSTEM },
  { label: '业务模块', value: ModuleTypeEnum.BUSINESS },
  { label: '工具模块', value: ModuleTypeEnum.TOOL },
  { label: '插件模块', value: ModuleTypeEnum.PLUGIN }
])

/** 获取类型标签样式 */
const getTypeTagType = (type?: number) => {
  const map: Record<number, string> = {
    [ModuleTypeEnum.SYSTEM]: '',
    [ModuleTypeEnum.BUSINESS]: 'success',
    [ModuleTypeEnum.TOOL]: 'warning',
    [ModuleTypeEnum.PLUGIN]: 'info'
  }
  return type ? map[type] || '' : ''
}

/** 获取状态标签样式 */
const getStatusTagType = (status?: number) => {
  const map: Record<number, string> = {
    [ModuleStatusEnum.NOT_INSTALLED]: 'info',
    [ModuleStatusEnum.INSTALLED]: 'warning',
    [ModuleStatusEnum.ENABLED]: 'success',
    [ModuleStatusEnum.DISABLED]: 'danger',
    [ModuleStatusEnum.FAILED]: 'danger',
    [ModuleStatusEnum.UNINSTALLING]: 'warning'
  }
  return status !== undefined ? map[status] || 'info' : 'info'
}

const isHomeCoreModule = (row: ModuleApi.ModuleVO) => isHomeCoreModuleEnabled(row)

const toggleHomeCoreModule = async (row: ModuleApi.ModuleVO, enabled: boolean) => {
  if (enabled) {
    // 跨分页统计已开启数，避免只数当前页导致超 12
    let openedCount = list.value.filter(
      (item) => item.id !== row.id && isHomeCoreModuleEnabled(item)
    ).length
    try {
      const data = await ModuleApi.getModulePage({ pageNo: 1, pageSize: 200 })
      openedCount = (data.list || []).filter(
        (item) => item.id !== row.id && isHomeCoreModuleEnabled(item)
      ).length
    } catch {
      /* 回落当前页计数 */
    }
    if (openedCount >= HOME_CORE_MODULE_LIMIT) {
      ElMessage.warning(`核心业务模块最多推荐 ${HOME_CORE_MODULE_LIMIT} 个`)
      return
    }
  }
  let config: Record<string, any> = {}
  try {
    config = row.config ? JSON.parse(row.config) : {}
  } catch {
    config = {}
  }
  if (!config || typeof config !== 'object') config = {}
  config.adminCard = { ...(config.adminCard || {}), homeShortcut: enabled }
  const next = JSON.stringify(config)
  try {
    await ModuleApi.updateModuleConfig(row.id!, next)
    row.config = next
    ElMessage.success(enabled ? '已加入核心业务模块' : '已移出核心业务模块')
  } catch {
    ElMessage.error('保存失败')
  }
}

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    const data = await ModuleApi.getModulePage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索 */
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

/** 重置 */
const resetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

/** 多选 */
let checkedIds: number[] = []
const handleSelectionChange = (selection: any[]) => {
  checkedIds = selection.map((item) => item.id)
}

/** 打开表单 */
const openForm = (type: string, id?: number) => {
  formRef.value?.open(type, id)
}

/** 启用模块 */
const handleEnable = async (id: number) => {
  try {
    await ElMessageBox.confirm('确认启用该模块？', '提示', { type: 'warning' })
    await ModuleApi.enableModule(id)
    ElMessage.success('启用成功')
    await refreshSidebar()
    getList()
  } catch {
    // 取消操作
  }
}

/** 禁用模块 */
const handleDisable = async (id: number) => {
  try {
    await ElMessageBox.confirm('确认禁用该模块？禁用后相关功能将不可用。', '提示', {
      type: 'warning'
    })
    await ModuleApi.disableModule(id)
    ElMessage.success('禁用成功')
    await refreshSidebar()
    getList()
  } catch {
    // 取消操作
  }
}

/** 安装模块 */
const handleInstall = async (id: number) => {
  try {
    await ElMessageBox.confirm('确认安装该模块？', '提示', { type: 'warning' })
    await ModuleApi.installModule(id)
    ElMessage.success('安装成功')
    getList()
  } catch {
    // 取消操作
  }
}

/** 卸载模块 */
const handleUninstall = async (id: number) => {
  try {
    await ElMessageBox.confirm(
      '确认卸载该模块？卸载后模块将恢复为未安装状态，租户仍可见并可重新安装。',
      '警告',
      {
        type: 'warning',
        confirmButtonText: '确定卸载',
        confirmButtonClass: 'el-button--danger'
      }
    )
    await ModuleApi.uninstallModule(id)
    ElMessage.success('卸载成功，模块已恢复为未安装状态')
    await refreshSidebar()
    getList()
  } catch {
    // 取消操作
  }
}

/**
 * 刷新侧边栏菜单
 * 重新生成路由并利用 Redirect 组件触发页面重渲染
 */
const refreshSidebar = async () => {
  await permissionStore.generateRoutes()
  // 利用已有 /redirect 路由强制刷新当前页面，触发侧边栏重新渲染
  const currentPath = window.location.pathname + window.location.search
  push('/redirect' + currentPath)
}

onMounted(() => {
  getList()
})
</script>
